import { s, h } from '../lib/sx'
import { A } from '../lib/router'
import type { SearchResult } from '../lib/search'
import { searchEmptyState } from '../content/copy'
import { config } from '../config'

const NAV = [
  ['/about', 'About'],
  ['/services', 'Services'],
  ['/sectors', 'Sectors'],
  ['/projects', 'Projects'],
  ['/safety', 'Safety'],
  ['/careers', 'Careers'],
  ['/news', 'News'],
  ['/contact', 'Contact'],
] as const

const CHROME_BUTTON =
  "height:38px;padding:0 14px;border:1px solid #3D4A59;background:transparent;color:#D3D8DE;cursor:pointer;display:flex;align-items:center;justify-content:center;font-family:'IBM Plex Mono',monospace;font-size:10.5px;letter-spacing:0.18em;text-transform:uppercase;white-space:nowrap;"

export interface HeaderProps {
  /** Viewport width, in CSS pixels. */
  w: number
  menu: boolean
  toggleMenu: () => void
  closeMenu: () => void
  searchOpen: boolean
  toggleSearch: () => void
  closeSearch: () => void
  q: string
  onSearch: (value: string) => void
  results: SearchResult[]
  noResults: boolean
}

export function Header(p: HeaderProps) {
  /** Below this the nav does not fit on one row alongside the logo and buttons. */
  const wide = p.w >= config.navBreakpoint
  /** Below this a search result reads better stacked than in two columns. */
  const stackResults = p.w < 600
  /** The logo is 3.8:1; below this it has to shrink to clear the buttons. */
  const LOGO_H = p.w >= config.wordmarkBreakpoint ? (wide ? 34 : 30) : 26
  const LOGO_W = Math.round(LOGO_H * 3.8)

  /**
   * Two arrangements rather than one that wraps.
   *
   * Wide: logo, then the search and quote cluster, then the nav — the
   * prototype's order, unchanged.
   *
   * Narrow: a single 64px row of logo + Search + Menu, with the quote call to
   * action moved into the menu panel where it gets full width. Letting the
   * prototype's wide arrangement wrap on its own produced a three-row, 180px
   * header at 320px, with the buttons stranded against the right edge.
   */
  return (
    <header style={s('position:sticky;top:0;z-index:60;background:#1B2430;color:#F2F4F6;border-bottom:1px solid #33404F;')}>
      <div
        style={s(
          wide
            ? 'max-width:1320px;margin:0 auto;padding:16px 24px;min-height:76px;box-sizing:border-box;display:flex;align-items:center;gap:24px;flex-wrap:wrap;row-gap:16px;'
            : 'max-width:1320px;margin:0 auto;padding:12px 18px;min-height:64px;box-sizing:border-box;display:flex;align-items:center;gap:12px;',
        )}
      >
        <A
          href="/"
          style={s('display:flex;align-items:center;flex:0 1 auto;min-width:0;')}
          onClick={p.closeMenu}
          aria-label="Samloryx Engineering — home"
        >
          <img
            src="/logo/samloryx-logo-round.svg"
            alt="Samloryx Engineering"
            width={LOGO_W}
            height={LOGO_H}
            style={s(`height:${LOGO_H}px;width:auto;display:block;`)}
          />
        </A>

        {wide && (
          <nav style={s('display:flex;flex-direction:row;align-items:center;width:auto;margin-left:auto;padding:0;gap:20px;font-size:13.5px;font-weight:500;letter-spacing:0.01em;flex-wrap:wrap;min-width:0;row-gap:2px;order:3;')}>
            {NAV.map(([href, label]) => (
              <A key={href} href={href} style={s('color:#D3D8DE;padding:6px 0;')} onClick={p.closeMenu} className={h('color:#FFFFFF;')}>
                {label}
              </A>
            ))}
          </nav>
        )}

        <div style={s(`display:flex;align-items:center;gap:${wide ? '14' : '8'}px;flex:0 0 auto;margin-left:auto;order:2;`)}>
          <button
            onClick={p.toggleSearch}
            style={s(CHROME_BUTTON + (!wide ? 'padding:0 12px;' : ''))}
            className={h('border-color:#E06A20;color:#FFFFFF;')}
            aria-expanded={p.searchOpen}
            aria-label="Search the site"
          >
            Search
          </button>

          {wide ? (
            <A
              href="/quote"
              style={s('background:#1F5C99;color:#FFFFFF;padding:11px 20px;font-size:13px;font-weight:600;letter-spacing:0.02em;white-space:nowrap;')}
              className={h('background:#17497A;color:#FFFFFF;')}
              onClick={p.closeMenu}
            >
              Request a quote
            </A>
          ) : (
            <button
              onClick={p.toggleMenu}
              style={s(CHROME_BUTTON + 'color:#F2F4F6;padding:0 12px;')}
              className={h('border-color:#E06A20;')}
              aria-expanded={p.menu}
              aria-controls="site-menu"
            >
              {p.menu ? 'Close' : 'Menu'}
            </button>
          )}
        </div>
      </div>

      {!wide && p.menu && (
        <nav id="site-menu" style={s('border-top:1px solid #33404F;background:#212C39;padding:8px 18px 18px;display:flex;flex-direction:column;')}>
          {NAV.map(([href, label]) => (
            <A
              key={href}
              href={href}
              style={s('color:#D3D8DE;padding:13px 0;font-size:16px;font-weight:500;border-bottom:1px solid #2A3746;')}
              onClick={p.closeMenu}
              className={h('color:#FFFFFF;')}
            >
              {label}
            </A>
          ))}
          <A
            href="/quote"
            style={s('background:#1F5C99;color:#FFFFFF;padding:15px;margin-top:18px;font-size:14px;font-weight:600;text-align:center;')}
            className={h('background:#17497A;color:#FFFFFF;')}
            onClick={p.closeMenu}
          >
            Request a quote
          </A>
        </nav>
      )}

      {p.searchOpen && (
        <div style={s('border-top:1px solid #33404F;background:#232F3D;padding:clamp(18px,4vw,26px) clamp(18px,4vw,32px);')}>
          <div style={s('max-width:1320px;margin:0 auto;')}>
            <input
              value={p.q}
              onChange={(e) => p.onSearch(e.target.value)}
              placeholder="Search projects, services, sectors, jobs…"
              autoFocus
              aria-label="Search the site"
              style={s('width:100%;background:transparent;border:0;border-bottom:1px solid #3D4A59;color:#F2F4F6;font-family:Archivo,sans-serif;font-size:clamp(20px,5.4vw,30px);font-weight:600;padding:8px 0 16px;outline:none;')}
            />
            <div style={s('margin-top:18px;display:flex;flex-direction:column;gap:2px;max-height:340px;overflow:auto;')}>
              {p.results.map((r) => (
                <A
                  key={`${r.kind}:${r.href}:${r.title}`}
                  href={r.href}
                  onClick={p.closeSearch}
                  style={s(`display:flex;gap:${stackResults ? '4px' : '18px'};flex-direction:${stackResults ? 'column' : 'row'};align-items:${stackResults ? 'flex-start' : 'baseline'};padding:12px 14px;background:#1B2430;color:#D3D8DE;`)}
                  className={h('background:#1F5C99;color:#FFFFFF;')}
                >
                  <span style={s(`font-family:'IBM Plex Mono',monospace;font-size:10px;letter-spacing:0.14em;text-transform:uppercase;color:#8C97A4;${stackResults ? '' : 'min-width:110px;'}`)}>{r.kind}</span>
                  <span style={s('font-size:15px;font-weight:500;')}>{r.title}</span>
                </A>
              ))}
              {p.noResults && (
                <div style={s("font-family:'IBM Plex Mono',monospace;font-size:12px;color:#8C97A4;padding:12px 0;")}>
                  {searchEmptyState}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
